import React, { useState, useEffect, memo } from "react";
import {
  Card,
  Badge,
  ButtonGroup,
  Form,
  Dropdown,
  Button,
} from "react-bootstrap";
import "./styles.scss";
import { DownloadButtons } from "./constants";
import { downloadPdf } from "./hooks";
import { getTroubleSpots } from "../../../utils/assessments";
import { getSingalClassroomData } from "../../../utils/classrooms";
import Loader from "../../../components/Loader";
import EmptyScreen from "../../../components/EmptyScreen";
import { useNavigate } from "react-router-dom";
import StyledOutlineButton from "../../../components/Classroom/StyledOutlineButton";
import BsCheckCircleFill from "../../../constants/icons/Bs/BsCheckCircleFill";
import BsXCircleFill from "../../../constants/icons/Bs/BsXCircleFill";
import { BsBook } from "../../../constants/icons/Bs/BsBook";
import BsClock from "../../../constants/icons/Bs/BsClock";
import BsFilter from "../../../constants/icons/Bs/BsFilter";
import { toast } from "react-toastify";

// Helper function to round scores to at most 1 decimal place
const roundScore = (score) => {
  if (typeof score === "number") {
    return Math.round(score * 10) / 10;
  }
  return score;
};

const TroubleSpots = ({ classroomId, isMobileView, studentId }) => {
  const [troubleSpots, setTroubleSpots] = useState([]);
  const [filteredSpots, setFilteredSpots] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const navigate = useNavigate();

  // Function to generate the correct URL structure for tests
  const generateTestUrl = (spot) => {
    return `/student/myClassrooms/${spot.classroomId}/${spot.subjectId}/${spot.topicId}/test/${spot._id}/${spot.examId}`;
  };

  // Function to generate the correct URL structure for practice
  const generatePracticeUrl = (spot) => {
    return `/student/myClassrooms/${spot.classroomId}/${spot.subjectId}/${spot.topicId}/practice/${spot._id}/${spot.examId}`;
  };

  // Fetch classroom details to get subjects
  useEffect(() => {
    const getClassroomDetails = async () => {
      try {
        const response = await getSingalClassroomData(classroomId);
        if (response?.data?.classRoom?.course?.subjects) {
          setSubjects(response.data.classRoom.course.subjects);
        }
      } catch (error) {
        console.error("Error fetching classroom details:", error);
        toast.error("Failed to load classroom details");
      }
    };

    if (classroomId) {
      getClassroomDetails();
    }
  }, [classroomId]);

  useEffect(() => {
    const fetchTroubleSpots = async () => {
      setIsLoading(true);
      try {
        if (classroomId) {
          const response = await getTroubleSpots(classroomId, studentId);
          if (response?.examAttempts.length > 0) {
            setTroubleSpots(response.examAttempts);
            setFilteredSpots(response.examAttempts);

            // Extract unique topics
            const uniqueTopics = [
              ...new Set(response.examAttempts.map((spot) => spot.topicName)),
            ].filter(Boolean);
            setTopics(uniqueTopics);
          } else {
            setTroubleSpots([]);
            setFilteredSpots([]);
            setTopics([]);
          }
        }
      } catch (error) {
        console.error("Error fetching trouble spots:", error.message);
        toast.error("Failed to load trouble spots");
      } finally {
        setIsLoading(false);
      }
    };
    if (classroomId) {
      fetchTroubleSpots();
    }
  }, [classroomId, studentId]);

  useEffect(() => {
    let filtered = troubleSpots;

    // Apply exam type filter
    if (activeFilter !== "all") {
      filtered = filtered.filter((spot) => spot.examType === activeFilter);
    }

    // Apply subject filter
    if (filteredSubjects.length > 0) {
      filtered = filtered.filter((spot) =>
        filteredSubjects.includes(spot.subjectId),
      );

      // Update topics based on selected subjects
      const topicsFromSelectedSubjects = filtered
        .map((spot) => spot.topicName)
        .filter(Boolean);

      const uniqueTopics = [...new Set(topicsFromSelectedSubjects)];
      setTopics(uniqueTopics);

      // Reset selected topic if it's no longer in the filtered list
      if (selectedTopic && !uniqueTopics.includes(selectedTopic)) {
        setSelectedTopic("");
      }
    } else {
      // If no subjects selected, reset to all topics from all spots
      const allTopics = troubleSpots
        .map((spot) => spot.topicName)
        .filter(Boolean);

      const uniqueTopics = [...new Set(allTopics)];
      setTopics(uniqueTopics);
    }

    // Apply topic filter
    if (selectedTopic) {
      filtered = filtered.filter((spot) => spot.topicName === selectedTopic);
    }

    setFilteredSpots(filtered);
  }, [activeFilter, filteredSubjects, selectedTopic, troubleSpots]);

  // Handle subject filter change
  const handleFilterChange = (subjectId) => {
    setFilteredSubjects((prevSelected) => {
      const isSelected = prevSelected.includes(subjectId);
      return isSelected
        ? prevSelected.filter((id) => id !== subjectId)
        : [...prevSelected, subjectId];
    });
  };

  DownloadButtons[0].onClick = () => {
    downloadPdf("Trouble-spots");
  };

  const getExamTypeBadge = (examType) => {
    const badges = {
      test: { color: "primary", text: "Topic-wise Test" },
      practice: { color: "success", text: "Practice" },
    };
    return badges[examType] || { color: "secondary", text: examType };
  };

  const truncateText = (text, maxLength = 40) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      style={{ margin: "10px", paddingBottom: isMobileView ? "20px" : "" }}
      id="Trouble-spots"
    >
      <div className="trouble-spots-content">
        {troubleSpots.length > 0 ? (
          <>
            <div className="filter-section">
              <div className="p-4">
                <div className="filter-buttons mb-3">
                  <ButtonGroup>
                    <button
                      className={`filter-btn ${
                        activeFilter === "all" ? "active" : ""
                      }`}
                      onClick={() => setActiveFilter("all")}
                    >
                      All
                    </button>
                    <button
                      className={`filter-btn ${
                        activeFilter === "test" ? "active" : ""
                      }`}
                      onClick={() => setActiveFilter("test")}
                    >
                      Topic-wise Tests
                    </button>
                    <button
                      className={`filter-btn ${
                        activeFilter === "practice" ? "active" : ""
                      }`}
                      onClick={() => setActiveFilter("practice")}
                    >
                      Practice
                    </button>
                  </ButtonGroup>
                </div>
                <div className="d-flex gap-3">
                  <Dropdown className="filter-dropdown">
                    <Dropdown.Toggle variant="light" className="filter-button">
                      <div className="filter-button-content">
                        <BsFilter className="filter-icon" />
                        <span className="filter-text ms-2">
                          Filter by Subject
                        </span>
                        {filteredSubjects.length > 0 && (
                          <span className="filter-badge ms-2">
                            {filteredSubjects.length}
                          </span>
                        )}
                      </div>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="filter-menu" align="end">
                      <div className="filter-menu-header">
                        <div className="filter-menu-title">
                          <BsFilter className="filter-menu-icon" />
                          <h5>Filter Subjects</h5>
                        </div>
                        {filteredSubjects.length > 0 && (
                          <Button
                            variant="link"
                            size="sm"
                            className="clear-all"
                            onClick={() => setFilteredSubjects([])}
                          >
                            Clear All
                          </Button>
                        )}
                      </div>
                      <div className="filter-menu-divider" />
                      <div className="filter-options">
                        {subjects.map((subject) => (
                          <div key={subject._id} className="filter-option">
                            <Form.Check
                              type="checkbox"
                              id={`subject-${subject._id}`}
                              label={subject.name}
                              checked={filteredSubjects.includes(subject._id)}
                              onChange={() => handleFilterChange(subject._id)}
                              className="custom-checkbox"
                            />
                          </div>
                        ))}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Form.Select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="filter-select w-25"
                  >
                    <option value="">All Topics</option>
                    {topics.map((topic, index) => (
                      <option key={index} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            </div>
            <div className="exam-attempts-grid">
              {filteredSpots.length > 0 ? (
                filteredSpots.map((spot, index) => {
                  const badge = getExamTypeBadge(spot.examType);
                  return (
                    <Card key={index} className="exam-attempt-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="exam-title-container flex-grow-1">
                            <h5 className="card-title mb-1" title={spot.name}>
                              {truncateText(spot.name)}
                            </h5>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              <Badge bg={badge.color}>{badge.text}</Badge>
                              {spot.examType === "assessment" ? (
                                spot.subjectName && (
                                  <Badge bg="info">
                                    {truncateText(spot.subjectName)}
                                  </Badge>
                                )
                              ) : (
                                <>
                                  <Badge bg="info">
                                    {truncateText(spot.subjectName)}
                                  </Badge>
                                  <Badge bg="secondary">
                                    {truncateText(spot.topicName || "Topic")}
                                  </Badge>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="exam-stats ms-3">
                            <div className="stat-item">
                              {spot?.type === 'writing' || spot?.type === 'speaking' ? '' : <>
                                <BsCheckCircleFill className="text-success" />
                                <span>{roundScore(spot.correct || 0)}</span>
                              </>}
                            </div>
                            <div className="stat-item">
                              {spot?.type === 'writing' || spot?.type === 'speaking' ? '' : <>
                                <BsXCircleFill className="text-danger" />
                                <span>
                                  {(spot.totalQuestions || 0) -
                                    (spot.correct || 0)}
                                </span>
                              </>}
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="detail-item gap-2 d-flex align-items-center">
                              <BsBook className="text-primary" />
                              {spot?.type === 'writing' || spot?.type === 'speaking' ? <span>{`Band: ${spot?.score}`}</span> :
                              <span>
                                {roundScore(spot.correct || 0)}/
                                {spot.totalQuestions || 0} Questions
                              </span>}
                            </div>
                            <div className="detail-item gap-2 d-flex align-items-center">
                              <BsClock className="text-secondary" />
                              <span>
                                {new Date(spot.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div
                            className="progress mb-3"
                            style={{ height: "6px" }}
                          >
                            {spot.examType === "assessment" ? (
                              <>
                                <div
                                  className="progress-bar bg-success"
                                  role="progressbar"
                                  style={{
                                    width: `${
                                      (roundScore(spot.correct || 0) /
                                        (spot.totalQuestions || 1)) *
                                      100
                                    }%`,
                                  }}
                                />
                                <div
                                  className="progress-bar bg-warning"
                                  role="progressbar"
                                  style={{
                                    width: `${
                                      (((spot.answeredQuestionsCount || 0) -
                                        (spot.correct || 0)) /
                                        (spot.totalQuestions || 1)) *
                                      100
                                    }%`,
                                  }}
                                />
                              </>
                            ) : (
                              <>
                                <div
                                  className="progress-bar bg-success"
                                  role="progressbar"
                                  style={{
                                    width: `${spot?.type === 'writing' || spot?.type === 'speaking' ? (((spot?.score || 0) / 9) * 100) :
                                      (roundScore(spot.correct || 0) /
                                        (spot.totalQuestions || 1)) *
                                      100
                                    }%`,
                                  }}
                                />
                                <div
                                  className="progress-bar bg-danger"
                                  role="progressbar"
                                  style={{
                                    width: `${
                                      ((spot?.type === 'writing' || spot?.type === 'speaking' ? spot?.score :((spot.totalQuestions || 0) -
                                        (spot.correct || 0))) /
                                        ((spot?.type === 'writing' || spot?.type === 'speaking' ? 9 : spot.totalQuestions) || 1)) *
                                      100
                                    }%`,
                                  }}
                                />
                              </>
                            )}
                          </div>
                          {spot?.type !== 'writing' && spot?.type !== 'speaking' && (
                            <StyledOutlineButton
                              onClick={() => {
                                if (spot.examType === "assessment") {
                                  navigate(
                                    `/student/${spot.classroomId}/exams/${
                                      spot?._id
                                    }/result?data=${encodeURIComponent(
                                      spot?.url,
                                    )}`,
                                  );
                                } else if (spot.examType === "test") {
                                  navigate(generateTestUrl(spot));
                                } else {
                                  // practice type
                                  navigate(generatePracticeUrl(spot));
                                }
                              }}
                              classRoomId={classroomId}
                              subjects={[spot.subjectId]}
                              btnText={"View Details"}
                              className="w-100"
                            />
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })
              ) : (
                <div className="no-results-message">
                  <h4>No Results Found</h4>
                  <p>Try adjusting your filters to see more results</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <EmptyScreen
            heading="No Trouble Spots Available"
            subHeading="You haven't attempted any exams or practices yet. Start practicing to identify areas that need improvement."
          />
        )}
      </div>
    </div>
  );
};

export default memo(TroubleSpots);
